import React, { createContext, useContext, useState } from "react";

const ViewModeContext = createContext(null);
const STORAGE_KEY = "flowpi_view_mode";
export const CANONICAL_DESKTOP_VIEWPORT_WIDTH = 1536;
const NORMAL_VIEWPORT_CONTENT = "width=device-width, initial-scale=1";

export function isPhoneLikeDevice(navigatorValue = navigator) {
  if (navigatorValue?.userAgentData?.mobile === true) return true;
  const userAgent = navigatorValue?.userAgent || "";
  return /iPhone|iPod|Android.*Mobile/i.test(userAgent);
}

export function detectInitialViewMode({ navigatorValue = navigator, width = window.innerWidth } = {}) {
  return isPhoneLikeDevice(navigatorValue) && width < 768 ? "mobile" : "desktop";
}

export function ViewModeProvider({ children }) {
  const [manualMode, setManualMode] = useState(() => sessionStorage.getItem(STORAGE_KEY));
  const automaticMode = detectInitialViewMode();
  const mode = manualMode === "mobile" || manualMode === "desktop" ? manualMode : automaticMode;
  const setMode = (nextMode) => {
    sessionStorage.setItem(STORAGE_KEY, nextMode);
    setManualMode(nextMode);
  };

  // Tailwind breakpoints resolve against the layout viewport, not a child canvas.
  // Only an explicit Desktop override on a phone receives the canonical desktop
  // layout viewport. User scaling remains enabled because no zoom restrictions are set.
  React.useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return undefined;
    const normalContent = viewport.getAttribute("content") || NORMAL_VIEWPORT_CONTENT;
    const phoneLike = isPhoneLikeDevice();
    if (manualMode === "desktop" && phoneLike) {
      viewport.setAttribute("content", `width=${CANONICAL_DESKTOP_VIEWPORT_WIDTH}, initial-scale=1`);
    } else {
      viewport.setAttribute("content", normalContent);
    }
    return () => viewport.setAttribute("content", normalContent);
  }, [manualMode]);

  return <ViewModeContext.Provider value={{ mode, setMode, manualMode, automaticMode }}>{children}</ViewModeContext.Provider>;
}

export function useViewMode() {
  const context = useContext(ViewModeContext);
  if (!context) throw new Error("useViewMode must be used inside ViewModeProvider");
  return context;
}
